import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type CandidateJobView, type PipelineView } from '../../lib/client.ts';
import { useMembers, usePipeline, usePipelines } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { AddToJobDialog } from '../projects/shortlist-dialogs.tsx';
import { describeAdd } from '../../lib/shortlists.ts';
import { StageChip } from '../pipelines/stage-chip.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';

const SOURCE: Record<string, string> = {
  ui: 'in the app',
  api: 'by the API',
  import: 'by an import',
  rule: 'by a rule',
};

/**
 * Candidate → Jobs (RELAY-953): every shortlist the candidate is on, one line each, with the
 * Shortlist Flow stage they are in there. The same rows the job's Shortlists tab shows, read by
 * candidate. A line expands to that place's stage history: who moved them, how, and when.
 */
export function CandidateJobs({ candidateId, name }: { candidateId: string; name?: string }) {
  const session = useSession();
  const qc = useQueryClient();
  const pipeline = usePipeline('shortlist');
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-4">
      {session.can('job.update') && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {notice ? (
            <p className="text-state-success-fg text-sm" role="status">
              {notice}
            </p>
          ) : (
            <span />
          )}
          <Button size="sm" onClick={() => setAdding(true)} data-testid="candidate-add-to-job">
            <Plus /> Add to job
          </Button>
        </div>
      )}
      <CandidateJobList candidateId={candidateId} pipeline={pipeline} />
      {adding && (
        <AddToJobDialog
          candidateIds={[candidateId]}
          label={name ?? 'this candidate'}
          pipeline={pipeline}
          onClose={() => setAdding(false)}
          onDone={async (result, jobTitle) => {
            setAdding(false);
            setNotice(describeAdd(result, jobTitle));
            await Promise.all(
              [['candidate-jobs', candidateId], ['job-shortlists'], ['board']].map((queryKey) =>
                qc.invalidateQueries({ queryKey }),
              ),
            );
          }}
        />
      )}
    </div>
  );
}

function CandidateJobList({
  candidateId,
  pipeline,
}: {
  candidateId: string;
  pipeline: PipelineView | undefined;
}) {
  const q = useQuery({
    queryKey: ['candidate-jobs', candidateId],
    queryFn: () => api.jobs.ofCandidate(candidateId),
  });
  if (q.isPending)
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </p>
    );
  if (q.error)
    return (
      <p className="text-danger text-sm" role="alert">
        {describeError(q.error)}
      </p>
    );
  if (q.data.length === 0)
    return (
      <p className="text-muted-foreground text-sm" data-testid="no-candidate-jobs">
        This candidate is not on any shortlist yet.
      </p>
    );
  return (
    <ul
      className="divide-border border-border bg-card shadow-card overflow-hidden rounded-card border divide-y"
      data-testid="candidate-jobs"
    >
      {q.data.map((place) => (
        <Place
          key={`${place.shortlist.id}:${place.job.id}`}
          place={place}
          candidateId={candidateId}
          pipeline={pipeline}
        />
      ))}
    </ul>
  );
}

function Place({
  place,
  candidateId,
  pipeline,
}: {
  place: CandidateJobView;
  candidateId: string;
  pipeline: PipelineView | undefined;
}) {
  const [open, setOpen] = useState(false);
  // The chip takes the colour of the pipeline its stage is in: a Candidate Flow one for a place
  // made before TS-1888, the default Shortlist Flow's otherwise.
  const stagePipeline =
    usePlacePipelines().find((p) => p.stages.some((s) => s.id === place.stageId)) ?? pipeline;
  return (
    <li data-testid="candidate-job">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} stage history for ${place.job.title}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <ChevronDown /> : <ChevronRight />}
        </Button>
        <Link
          to="/projects/$id"
          params={{ id: place.job.projectId }}
          search={{ job: place.job.id }}
          className="min-w-40 flex-1 font-medium hover:underline"
        >
          {place.job.title}
        </Link>
        <span className="text-muted-foreground text-xs">{place.shortlist.name}</span>
        {stagePipeline ? (
          <StageChip
            family={stagePipeline.family}
            colour={place.stage.colour as never}
            name={place.stage.name}
            terminalKind={place.stage.terminalKind}
          />
        ) : (
          place.stage.name
        )}
        <span className="text-muted-foreground text-xs">
          since {new Date(place.stageEnteredAt).toLocaleDateString()}
        </span>
        {place.visibleToClient && <Badge variant="secondary">Client sees</Badge>}
      </div>
      {place.rejectedReason && (
        <p className="text-muted-foreground px-12 pb-2 text-xs">Reason: {place.rejectedReason}</p>
      )}
      {open && <StageHistory shortlistId={place.shortlist.id} candidateId={candidateId} />}
    </li>
  );
}

/**
 * Every live Shortlist Flow AND Candidate Flow pipeline: a place made before TS-1888 may still
 * sit in a Candidate Flow stage, and its history names them.
 */
function usePlacePipelines(): PipelineView[] {
  return [...usePipelines('shortlist'), ...usePipelines('candidate')];
}

function StageHistory({ shortlistId, candidateId }: { shortlistId: string; candidateId: string }) {
  const { nameOf } = useMembers();
  const stages = usePlacePipelines().flatMap((p) => p.stages);
  const q = useQuery({
    queryKey: ['candidate-stage-history', shortlistId, candidateId],
    queryFn: () => api.jobs.stageHistory(shortlistId, candidateId),
  });
  const name = (id: string | null) =>
    id ? (stages.find((s) => s.id === id)?.name ?? 'Removed stage') : null;
  if (q.isPending) return <p className="text-muted-foreground px-12 pb-2 text-xs">Loading…</p>;
  if (q.error)
    return (
      <p className="text-danger px-12 pb-2 text-xs" role="alert">
        {describeError(q.error)}
      </p>
    );
  return (
    <ol className="flex flex-col gap-1 px-12 pb-3 text-xs" data-testid="stage-history">
      {[...q.data].reverse().map((h) => (
        <li key={h.id} className="text-muted-foreground">
          <time dateTime={h.createdAt}>{new Date(h.createdAt).toLocaleString()}</time>:{' '}
          <span className="text-foreground">
            {h.oldStageId
              ? `${name(h.oldStageId)} → ${name(h.newStageId)}`
              : `Added in ${name(h.newStageId)}`}
          </span>{' '}
          {SOURCE[h.source] ?? h.source}
          {h.changedBy ? `, by ${nameOf(h.changedBy)}` : ''}
        </li>
      ))}
    </ol>
  );
}
